import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styles from './styles.module.css';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import TerminalService from 'services/TerminalServices';
import FeatureNotAvailableDialog from 'components/popups/FeatureNotAvailableDialog';
import { AlertMessagesContext } from 'react-alert-messages';
import {
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  FormGroup,
  Checkbox,
  Button,
  Skeleton,
  Typography,
} from '@mui/material';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import { ShopContext } from 'Context/ShopContext';
import { WarehouseContext } from 'Context/WarehouseContext';
import {
  ORDER_TYPES,
  BARCODE_PRINTER_FORMATS,
  BARCODE_PRINTER_TYPES,
  KOT_FORMATS,
  INVOICE_FORMATS,
  PRINTER_TYPES,
  SUFFIX_OPTIONS,
} from 'const';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link } from 'react-router-dom';
import { ROUTES } from 'const';
import TableService from 'services/TableService';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';
import ShopsHelper from 'helpers/ShopsHelper';
import ShopSettingsService from 'services/ShopSettingsService';
import AccountServices from 'services/AccountServices'

const TERMINAL_TYPES = {
  POS: 'pos',
  ECS: 'ecs',
};

function TerminalSettings() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const [isLoading, setIsLoading] = useState(false);
  const [featureNotAvailable, setFeatureNotAvailable] = useState(false);
  const [terminal, setTerminal] = useState(null);
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [paymentModes, setPaymentModes] = useState([]);
  const [accountHeads, setAccountHeads] = useState([]);
  const [config, setConfig] = useState({
    pinelabs_cloud_payment_config: { client_id: '', merchant_id: '', security_token: '', store_id: '' },
  });

  const { terminalId } = useParams();
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { getWarehouses, warehouses } = useContext(WarehouseContext);

  const loadTableAreas = useCallback(async () => {
    try {
      const res = await TableService.getAreas();
      setAreas(res);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
  }, [postAlertMessage]);

  const getTerminal = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await TerminalService.getTerminal(terminalId);
      if (res) {
        if (res.default_warehouse) {
          res.default_warehouse_id = res.default_warehouse.id;
        }
        setTerminal(res);
        if (res.config) {
          setConfig({
            ...res.config,
            pinelabs_cloud_payment_config: res.config.pinelabs_cloud_payment_config || {
              client_id: '',
              merchant_id: '',
              security_token: '',
              store_id: '',
            },
          });
        }
        if (res.type === TERMINAL_TYPES.POS) {
          await loadTableAreas();
          setSelectedArea(res.default_area?.id || null);
        }
      }
    } catch (error) {
      postAlertMessage({ text: tCommon('Attempt failed'), type: 'failed' });
    }
    setIsLoading(false);
  }, [terminalId, postAlertMessage, loadTableAreas, tCommon]);

  const updateTerminal = async () => {
    setIsLoading(true);
    try {
      const res = await TerminalService.updateTerminals(terminalId, {
        ...(terminal || {}),
        default_area_id: selectedArea,
        config: config,
      });
      if (res && res.default_warehouse) {
        res.default_warehouse_id = res.default_warehouse.id;
      }
      setTerminal(res);
      if (res.config) {
        setConfig({
          ...res.config,
          pinelabs_cloud_payment_config: res.config.pinelabs_cloud_payment_config || {
            client_id: '',
            merchant_id: '',
            security_token: '',
            store_id: '',
          },
        });
      }
      postAlertMessage({
        text: tCommon('Updated terminal successfully'),
        type: 'success',
      });
    } catch (error) {
      postAlertMessage({ text: tCommon('Attempt failed'), type: 'failed' });
    }
    setIsLoading(false);
  };

  const featureIsNotAvailable = (validity, feature) => {
    if (featureIsExpired(validity)) {
      setFeatureNotAvailable(feature);
    }
  };

  const availableOrderTypes =
    shop?.config?.order_types && Object.keys(shop.config.order_types).length > 0
      ? Object.keys(shop.config.order_types).map((name) => {
          const defaultType = ORDER_TYPES.find((ot) => Object.keys(ot)[0] === name);
          return defaultType ? defaultType : { [name]: name };
        })
      : ORDER_TYPES;

  const orderTypeHandler = (event) => {
    const { name } = event.target;
    let orderTypes;

    if (terminal?.order_types != null && typeof terminal.order_types === 'string') {
      orderTypes = terminal.order_types.split(',');
    } else {
      orderTypes = [];
    }

    if (orderTypes.includes(name)) {
      orderTypes = orderTypes.filter((type) => type !== name);
    } else {
      orderTypes.push(name);
    }

    let activeOrderTypes = orderTypes.join(',');
    setTerminal({ ...terminal, order_types: activeOrderTypes });
  };

  const isCheckedOrderType = (order_type) => {
    if (terminal?.order_types !== null && typeof terminal?.order_types === 'string') {
      return terminal?.order_types.split(',').includes(order_type);
    } else {
      return false;
    }
  };

  const handleChangeMode = (event) => {
    const { name, checked } = event.target;
    const updatedData = {
      ...terminal,
      [name]: checked,
    };

    setTerminal(updatedData);
  };

  const closeDialogHandler = () => {
    setFeatureNotAvailable(null);
  };

  const disconnectTerminalhandler = async () => {
    setIsLoading(true);
    try {
      await TerminalService.disconnectTerminal(terminalId);
    } catch (error) {
      postAlertMessage({ text: tCommon('Attempt failed'), type: 'failed' });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getTerminal();
  }, [getTerminal]);

  useEffect(() => {
    const fetchPaymentModes = async () => {
      try {
        const res = await ShopSettingsService.getPaymentModes({ all: true });
        setPaymentModes(res);
      } catch (error) {
        console.error(error);
      }
    };
    const fetchAccountHeads = async () => {
      try {
        const res = await AccountServices.getAccountHeads({ is_enabled: true });
        setAccountHeads(res || []);
      } catch (error) {
        console.error(error);
      }
    };
    fetchPaymentModes();
    fetchAccountHeads();
  }, []);

  useEffect(() => {
    if (ShopsHelper.isWarehouseFeatureActive(shop)) {
      getWarehouses();
    }
  }, [shop, getWarehouses]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <Link to={ROUTES.TERMINALS} className={styles.link}>
          <ArrowBackIcon />
        </Link>
        <h2 className={styles.title}>
          {tCommon('Terminal')} <span className={styles.menuTitle}>{tCommon('Terminal Details')}</span>
        </h2>
      </div>
      <div className={styles.settingsGrid}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6} className={styles.BillHeader}>
            <Grid container spacing={2} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>ID</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="id"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled
                  value={terminal?.id || ''}
                />
                {isLoading && !terminal && <Skeleton variant="text" width="100%" height={40} animation="wave" />}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={6} className={styles.BillHeader}>
            <Grid container spacing={2} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>Name</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="id"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(e) => {
                    setTerminal({ ...terminal, name: e.target.value });
                  }}
                  value={terminal?.name || ''}
                />
                {isLoading && !terminal && <Skeleton variant="text" width="100%" height={40} animation="wave" />}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={6} className={styles.BillHeader}>
            <Grid container spacing={2} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>Type</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="id"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled
                  value={terminal?.type || ''}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={6} className={styles.BillHeader}>
            <Grid container spacing={2} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>{tCommon('Mac ID')}</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={tCommon('Mac ID')}
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(e) => {
                    setTerminal({ ...terminal, mac_id: e.target.value });
                  }}
                  value={terminal?.mac_id || ''}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={6} className={styles.BillHeader}>
            <Grid container spacing={2} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>{tCommon('Access code')}</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={tCommon('Access code')}
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(e) => {
                    setTerminal({ ...terminal, access_code: e.target.value });
                  }}
                  value={terminal?.access_code || ''}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={6} className={styles.BillHeader}>
            <Grid container spacing={2} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>{tCommon('Admin code')}</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={tCommon('Admin code')}
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(e) => {
                    setTerminal({ ...terminal, admin_code: e.target.value });
                  }}
                  value={terminal?.admin_code || ''}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={6} className={styles.BillHeader}>
            <Grid container spacing={2} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>{tCommon('Location')}</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={tCommon('Location')}
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled
                  onChange={(e) => {
                    setTerminal({ ...terminal, location: e.target.value });
                  }}
                  value={terminal?.location || ''}
                />
              </Grid>
            </Grid>
          </Grid>
          {terminal?.type === TERMINAL_TYPES.ECS && (
            <>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Connected Host')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      name={tCommon('Connected Host')}
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      disabled
                      value={terminal?.connected_host || ''}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Connected Server Port')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      name={tCommon('Connected Server Port')}
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      disabled
                      value={terminal?.connected_server_port || ''}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Communication Port')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      name={tCommon('Communication Port')}
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      disabled
                      value={terminal?.connected_comm_port || ''}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Next FID')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      name={tCommon('Next FID')}
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      value={terminal?.next_sale_number_to_load || ''}
                      onChange={(event) => {
                        setTerminal({
                          ...terminal,
                          next_sale_number_to_load: event.target.value,
                        });
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
          <Grid item xs={12} lg={6} className={styles.BillHeader}>
            <Grid container spacing={2} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>{tCommon('Is active')}</span>
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={terminal?.is_active || false}
                      // onChange={handleChangeTerminalMode}
                      // name={'is_acitve'}
                      disabled
                      color="primary"
                    />
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={6} className={styles.BillHeader}>
            <Grid container spacing={2} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>Allow force attach</span>
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.allow_force_attach ?? false}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          allow_force_attach: e.target.checked,
                        })
                      }
                      color="primary"
                    />
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <div className={styles.titleSec} style={{ marginTop: 20 }}>
        <h2 className={styles.title}>
          {tCommon('Terminal')} <span className={styles.menuTitle}>{tCommon('Terminal settings')}</span>
        </h2>
      </div>
      <div className={styles.settingsGrid}>
        <Grid container spacing={2} display="flex" alignItems="center">
          <Grid item xs={12} className={styles.subHeader}>
            <span>{tCommon('Invoice Config')}</span>
          </Grid>
          <Grid item xs={12} lg={6} className={styles.BillHeader}>
            <Grid container spacing={2} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>{tCommon('Invoice Number Prefix')}</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="invoice_no_prefix"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setTerminal({
                      ...terminal,
                      invoice_no_prefix: event.target.value,
                    });
                  }}
                  value={terminal?.invoice_no_prefix || ''}
                />
              </Grid>
            </Grid>
          </Grid>

          {terminal?.type === TERMINAL_TYPES.ECS && (
            <>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Suffix Type For Bill Number')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      name="printerType"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onChange={(event) => {
                        setTerminal({
                          ...terminal,
                          suffix_type_for_bill_number: event.target.value,
                        });
                      }}
                      value={terminal?.suffix_type_for_bill_number || 'FID'}
                    >
                      {SUFFIX_OPTIONS.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
          {terminal?.type !== TERMINAL_TYPES.ECS && (
            <>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Token Number Prefix')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      name="printerType"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onChange={(event) => {
                        setTerminal({
                          ...terminal,
                          token_no_prefix: event.target.value,
                        });
                      }}
                      value={terminal?.token_no_prefix || ''}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} className={styles.subHeader}>
                <span>{tCommon('POS Config')}</span>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tCommon('Show Payment Page')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.show_payment_page || false}
                          onChange={handleChangeMode}
                          name={'show_payment_page'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tCommon('Show MRP Column On Invoice')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.show_mrp_column_on_invoice || false}
                          onChange={handleChangeMode}
                          name={'show_mrp_column_on_invoice'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tCommon('Counter Foil With Invoice')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.counter_foil_with_invoice || false}
                          onChange={handleChangeMode}
                          name={'counter_foil_with_invoice'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Show Payment Mode On Invoice')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.show_payment_mode_on_invoice || false}
                          onChange={handleChangeMode}
                          name={'show_payment_mode_on_invoice'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Print Total Save On Invoice')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.print_total_save_on_invoice || false}
                          onChange={handleChangeMode}
                          name={'print_total_save_on_invoice'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Skip Payment Page On Invoice')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.skip_payment_page_on_invoice || false}
                          onChange={handleChangeMode}
                          name={'skip_payment_page_on_invoice'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Order With Full Payment Only')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.order_with_full_payment_only || false}
                          onChange={handleChangeMode}
                          name={'order_with_full_payment_only'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Print Invoice On Payment Update')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.print_invoice_on_payment_update || false}
                          onChange={handleChangeMode}
                          name={'print_invoice_on_payment_update'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Price With Tax In Invoice')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.price_with_tax_in_invoice || false}
                          onChange={handleChangeMode}
                          name={'price_with_tax_in_invoice'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Amount With Three Fraction')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.amount_with_three_fraction || false}
                          onChange={handleChangeMode}
                          name={'amount_with_three_fraction'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Salesperson Login')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.salesperson_login || false}
                          onChange={handleChangeMode}
                          name={'salesperson_login'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Advanced Quantity Control')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.advanced_quantity_control || false}
                          onChange={handleChangeMode}
                          name={'advanced_quantity_control'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Bluetooth Scale Weight Confirmation')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.confirm_bluetooth_scale_weight || false}
                          onChange={handleChangeMode}
                          name={'confirm_bluetooth_scale_weight'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Enable Table Selection POS')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.enable_table_selection_pos || false}
                          onChange={handleChangeMode}
                          name={'enable_table_selection_pos'}
                          color="primary"
                          disabled={featureIsExpired(shop?.kot_validity)}
                        />
                      }
                      onClick={() => featureIsNotAvailable(shop?.kot_validity, 'KOT')}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Order Dashboard Enabled')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.is_order_dashboard_enabled || false}
                          onChange={handleChangeMode}
                          name={'is_order_dashboard_enabled'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Table Dashboard Enabled')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.is_table_dashboard_enabled || false}
                          onChange={handleChangeMode}
                          name={'is_table_dashboard_enabled'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Print kot with bill')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={terminal?.kot_with_bill || false}
                          onChange={handleChangeMode}
                          name={'kot_with_bill'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Print HSN</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.print_hsn || false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              print_hsn: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Consolidated KOT</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.consolidated_kot || false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              consolidated_kot: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Auto Receive Full Amount</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.auto_receive_full_amount || false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              auto_receive_full_amount: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Show Item Charges on Invoice</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.show_item_charges_on_invoice || false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              show_item_charges_on_invoice: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Show Payment QR Code on Invoice</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.print_payment_qr_on_invoice ?? false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              print_payment_qr_on_invoice: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Display Awaiting Dispatch Orders</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.display_awaiting_dispatch_orders ?? false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              display_awaiting_dispatch_orders: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Print Item Notes on Invoice</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.print_item_notes_on_invoice ?? false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              print_item_notes_on_invoice: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Low Memory Device</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.low_memory_device ?? false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              low_memory_device: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Offline Sales only</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.offline_sales_only ?? false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              offline_sales_only: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>ERP Dashboard Enabled</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.erp_dashboard_enabled ?? false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              erp_dashboard_enabled: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>PLU Search</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.plu_search ?? false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              plu_search: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Auto Update Weight and Quantity</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.auto_update_weight_and_quantity ?? false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              auto_update_weight_and_quantity: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Print Items on Table Change KOT</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.show_items_on_kot_when_table_change ?? false}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              show_items_on_kot_when_table_change: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Number of Shifts Store on POS')}</span>
                  </Grid>
                  <Grid item sm={6}>
                    <NumberField
                      name="number_of_shifts_to_be_stored_on_device"
                      color="primary"
                      onChange={(event) => {
                        setTerminal({
                          ...terminal,
                          number_of_shifts_to_be_stored_on_device: event.target.value,
                        });
                      }}
                      value={terminal?.number_of_shifts_to_be_stored_on_device || ''}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('UPI ID')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onChange={(event) => {
                        setTerminal({
                          ...terminal,
                          upi_id: event.target.value,
                        });
                      }}
                      value={terminal?.upi_id || ''}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Default Area')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      name="defaultArea"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onChange={(event) => {
                        setSelectedArea(Number(event.target.value));
                      }}
                      value={selectedArea || ''}
                    >
                      {areas?.map((area) => (
                        <MenuItem key={area.id} value={area.id}>
                          {area.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>
              {ShopsHelper.isWarehouseFeatureActive(shop) && (
                <Grid item xs={12} lg={6} className={styles.BillHeader}>
                  <Grid container spacing={2} display="flex" alignItems="center">
                    <Grid item xs={6}>
                      <span className={styles.fieldTitle}>{tCommon('Default Warehouse')}</span>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        select
                        name="default_warehouse_id"
                        size="small"
                        variant="outlined"
                        color="primary"
                        fullWidth
                        value={terminal?.default_warehouse_id || 'null'}
                        onChange={(event) => {
                          setTerminal({
                            ...terminal,
                            default_warehouse_id: event.target.value === 'null' ? null : Number(event.target.value),
                          });
                        }}
                      >
                        <MenuItem value="null">{tCommon('None')}</MenuItem>
                        {warehouses?.map((warehouse) => (
                          <MenuItem key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item sm={6}>
                    <span className={styles.fieldTitle}>Invoice Display Rate</span>
                  </Grid>
                  <Grid item sm={6}>
                    <TextField
                      select
                      name="invoice_display_rate"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onChange={(event) => {
                        setConfig({
                          ...config,
                          invoice_display_rate: event.target.value,
                        });
                      }}
                      value={config.invoice_display_rate || 'actual_rate'}
                    >
                      <MenuItem value="actual_rate">Actual Rate</MenuItem>
                      <MenuItem value="actual_rate_plus_tax">Actual Rate + Tax</MenuItem>
                      <MenuItem value="base_rate">Base Rate</MenuItem>
                      <MenuItem value="base_rate_plus_tax">Base Rate + Tax</MenuItem>
                      <MenuItem value="price">Price</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('Stable Weight Count')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <NumberField
                      name="stable_weight_count"
                      color="primary"
                      onChange={(e) => {
                        setConfig({ ...config, stable_weight_count: parseInt(e.target.value) });
                      }}
                      value={config?.stable_weight_count ?? ''}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tManagement('KOT Retry Count')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <NumberField
                      name="kot_retry_count"
                      color="primary"
                      onChange={(e) => {
                        setConfig({ ...config, kot_retry_count: parseInt(e.target.value)});
                      }}
                      value={config?.kot_retry_count ?? ''}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>Custom Notes</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <TextField
                      name="custom_notes"
                      size="small"
                      variant="outlined"
                      color="primary"
                      maxRows={5}
                      multiline
                      fullWidth
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          custom_notes: e.target.value,
                        })
                      }
                      value={config.custom_notes || ''}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item sm={6}>
                    <span className={styles.fieldTitle}>KOT Split Options</span>
                  </Grid>
                  <Grid item sm={6}>
                    <TextField
                      select
                      name="kot_split_options"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onChange={(event) => {
                        setConfig({
                          ...config,
                          kot_split_options: event.target.value,
                        });
                      }}
                      value={config.kot_split_options || ''}
                    >
                      <MenuItem value="kitchen">By Kitchen</MenuItem>
                      <MenuItem value="category">By Category</MenuItem>
                      <MenuItem value="item">By Item</MenuItem>
                      <MenuItem value="item_quantity">By Item Quantity</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={12} className={styles.BillHeader}>
                <Grid container spacing={2} display="flex" alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <span className={styles.fieldTitle}>{tCommon('Order Types')}</span>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormGroup row={true}>
                      {availableOrderTypes.map((order_type) => {
                        let [key, value] = Object.entries(order_type)[0];
                        return (
                          <FormControlLabel
                            key={key}
                            style={{ marginRight: '5%' }}
                            control={
                              <Checkbox
                                checked={isCheckedOrderType(value)}
                                name={value}
                                color="primary"
                                onChange={orderTypeHandler}
                              />
                            }
                            label={key}
                          />
                        );
                      })}
                    </FormGroup>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" style={{ marginBottom: 16 }}>
                  {tCommon('Payment Mode and Account Head Mappings')}
                </Typography>
              </Grid>
              {paymentModes.map((mode) => (
                <Grid item xs={12} lg={6} className={styles.BillHeader} key={mode.id}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <span className={styles.fieldTitle}>{mode.name}</span>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={config.payment_mode_account_head_mappings?.[mode.name] || ""}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            payment_mode_account_head_mappings: {
                              ...(config.payment_mode_account_head_mappings || {}),
                              [mode.name]: e.target.value,
                            },
                          })
                        }
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {accountHeads?.map((head) => (
                          <MenuItem key={head.id} value={head.id}>
                            {head.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Typography variant="h6" style={{ marginBottom: 16, marginTop: 16 }}>
                  {tCommon('Pine Labs Cloud Payment Configuration')}
                </Typography>
              </Grid>
              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>Client ID</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      type="number"
                      value={config.pinelabs_cloud_payment_config?.client_id || ''}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          pinelabs_cloud_payment_config: {
                            ...(config.pinelabs_cloud_payment_config || {}),
                            client_id: e.target.value ? Number(e.target.value) : '',
                          },
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>Merchant ID</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={config.pinelabs_cloud_payment_config?.merchant_id || ''}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          pinelabs_cloud_payment_config: {
                            ...(config.pinelabs_cloud_payment_config || {}),
                            merchant_id: e.target.value,
                          },
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>Security Token</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={config.pinelabs_cloud_payment_config?.security_token || ''}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          pinelabs_cloud_payment_config: {
                            ...(config.pinelabs_cloud_payment_config || {}),
                            security_token: e.target.value,
                          },
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={6} className={styles.BillHeader}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>Store ID</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={config.pinelabs_cloud_payment_config?.store_id || ''}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          pinelabs_cloud_payment_config: {
                            ...(config.pinelabs_cloud_payment_config || {}),
                            store_id: e.target.value,
                          },
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} className={styles.subHeader}>
                <span>{tCommon('Printer Config')}</span>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Printer Type')}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="printerType"
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        printer_type: event.target.value,
                      });
                    }}
                    value={terminal?.printer_type || 'USB'}
                  >
                    {PRINTER_TYPES.map((printer_type) => (
                      <MenuItem key={printer_type} value={printer_type}>
                        {printer_type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12} lg={4}>
                  <span className={styles.fieldTitle}>{tCommon('Invoice Format')}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="invoiceFormat"
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        invoice_format: event.target.value,
                      });
                    }}
                    value={terminal?.invoice_format || '3 Inch Image'}
                  >
                    {INVOICE_FORMATS.map((invoice_format) => (
                      <MenuItem key={invoice_format} value={invoice_format}>
                        {invoice_format}
                      </MenuItem>
                    ))}
                    {(terminal?.printer_type === 'PDF' || terminal?.printer_type === 'Whatsapp') && (
                      <MenuItem key={'Share'} value={'Share'}>
                        {'Share'}
                      </MenuItem>
                    )}
                  </TextField>
                </Grid>
              </Grid>

              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                {' '}
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Kitchen KOT Format')}</span>
                </Grid>
                <Grid item xs={12} onClick={() => featureIsNotAvailable(shop?.kot_validity, 'KOT')}>
                  <TextField
                    select
                    name="kitchenKotFomat"
                    size="small"
                    variant="outlined"
                    disabled={featureIsExpired(shop?.kot_validity)}
                    color="primary"
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        kitchen_kot_format: event.target.value,
                      });
                    }}
                    value={terminal?.kitchen_kot_format || '3 Inch'}
                  >
                    {KOT_FORMATS.map((kot_format) => (
                      <MenuItem key={kot_format} value={kot_format}>
                        {kot_format}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Consolidated KOT Format')}</span>
                </Grid>
                <Grid item xs={12} onClick={() => featureIsNotAvailable(shop?.kot_validity, 'KOT')}>
                  <TextField
                    select
                    name="consolidatedKotFomat"
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={featureIsExpired(shop?.kot_validity)}
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        consolidated_kot_format: event.target.value,
                      });
                    }}
                    value={terminal?.consolidated_kot_format || '3 Inch'}
                  >
                    {KOT_FORMATS.map((kot_format) => (
                      <MenuItem key={kot_format} value={kot_format}>
                        {kot_format}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('KOT With Invoice Printer IP')}</span>
                </Grid>
                <Grid item xs={12} style={{ position: 'relative' }}>
                  {featureIsExpired(shop?.kot_validity) && (
                    <div
                      className={styles.showDialog}
                      onClick={() => featureIsNotAvailable(shop?.kot_validity, 'KOT')}
                    />
                  )}
                  <TextField
                    name="barcodePrinterFormat"
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={featureIsExpired(shop?.kot_validity)}
                    fullWidth
                    onClick={() => featureIsNotAvailable(shop?.kot_validity, 'KOT')}
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        kot_with_invoice_printer_ip: event.target.value,
                      });
                    }}
                    value={terminal?.kot_with_invoice_printer_ip || ''}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Consolidated KOT Printer IP')}</span>
                </Grid>
                <Grid item xs={12} style={{ position: 'relative' }}>
                  {featureIsExpired(shop?.kot_validity) && (
                    <div
                      className={styles.showDialog}
                      onClick={() => featureIsNotAvailable(shop?.kot_validity, 'KOT')}
                    />
                  )}
                  <TextField
                    name="barcodePrinterFormat"
                    size="small"
                    variant="outlined"
                    disabled={featureIsExpired(shop?.kot_validity)}
                    color="primary"
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        consolidated_kot_printer_ip: event.target.value,
                      });
                    }}
                    value={terminal?.consolidated_kot_printer_ip || ''}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Barcode Printer Type')}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="barcodePrinterType"
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        barcode_printer_type: event.target.value,
                      });
                    }}
                    value={terminal?.barcode_printer_type || 'USB'}
                  >
                    {BARCODE_PRINTER_TYPES.map((barcode_printer_type) => (
                      <MenuItem key={barcode_printer_type} value={barcode_printer_type}>
                        {barcode_printer_type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Barcode Printer Format')}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="barcodePrinterFormat"
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        barcode_printer_format: event.target.value,
                      });
                    }}
                    value={terminal?.barcode_printer_format || '38 X 25 [2 column]'}
                  >
                    {BARCODE_PRINTER_FORMATS.map((barcode_printer_format) => (
                      <MenuItem key={barcode_printer_format} value={barcode_printer_format}>
                        {barcode_printer_format}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Barcode Custom Note')}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="barcodePrinterNote"
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        barcode_custom_note: event.target.value,
                      });
                    }}
                    value={terminal?.barcode_custom_note}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Custom PRN')}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    variant="outlined"
                    color="primary"
                    maxRows={5}
                    multiline
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        custom_prn: event.target.value,
                      });
                    }}
                    value={terminal?.custom_prn || ''}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Printer segment height')}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="barcodePrinterNote"
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        printer_segment_height: event.target.value,
                      });
                    }}
                    value={terminal?.printer_segment_height || ''}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Printer segment delay')}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="barcodePrinterNote"
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        printer_segment_delay: event.target.value,
                      });
                    }}
                    value={terminal?.printer_segment_delay || ''}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Printer cutter height')}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="barcodePrinterNote"
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        printer_cutter_height: event.target.value,
                      });
                    }}
                    value={terminal?.printer_cutter_height || ''}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={4} display="flex" alignItems="center">
                <Grid item xs={12}>
                  <span className={styles.fieldTitle}>{tCommon('Printer cutter delay')}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="barcodePrinterNote"
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onChange={(event) => {
                      setTerminal({
                        ...terminal,
                        printer_cutter_delay: event.target.value,
                      });
                    }}
                    value={terminal?.printer_cutter_delay || ''}
                  />
                </Grid>
              </Grid>
            </>
          )}
          <Grid item xs={12} className={styles.buttonWrapper} justifyContent="flex-end">
            {terminal?.type === TERMINAL_TYPES.ECS && (
              <Button
                variant="contained"
                color="error"
                className={styles.button}
                disabled={isLoading || !terminal}
                onClick={disconnectTerminalhandler}
              >
                {tCommon('Disconnect')}
              </Button>
            )}
            <Button
              variant="contained"
              color="success"
              className={styles.button}
              disabled={isLoading || !terminal}
              onClick={updateTerminal}
            >
              {tCommon('Update')}
            </Button>
          </Grid>
        </Grid>
        {featureNotAvailable && (
          <FeatureNotAvailableDialog closeHandler={closeDialogHandler} feature={featureNotAvailable} />
        )}
      </div>
    </div>
  );
}

export default withConsoleBase(TerminalSettings);
